<template>
  <div>
    <div class="articleSlider relative">
      <Swiper :settings="settings" :modules="[Navigation]" @swiper="onSwiper">
        <slot />
      </Swiper>
      <OButton
        v-if="!isMobile"
        ref="prevNavBtn"
        pure
        class="
          absolute
          left-0
          top-1/2
          -translate-y-1/2 -translate-x-1/2
          u-center
          w-12
          h-12
          rounded-full
          z-1
          text-white
          border-white border-4
          text-xl
        "
        aria-label="önceki"
        :style="{ background: arrowColor }"
      >
        <OIcon icon="chevron-left-white" />
      </OButton>
      <OButton
        v-if="!isMobile"
        ref="nextNavBtn"
        pure
        class="
          absolute
          right-0
          top-1/2
          -translate-y-1/2
          translate-x-1/2
          u-center
          w-12
          h-12
          rounded-full
          z-1
          text-white
          border-white border-4
          text-xl
        "
        aria-label="sonraki"
        :style="{ background: arrowColor }"
      >
        <OIcon icon="chevron-left-white" class="rotate-180" />
      </OButton>
    </div>
    <div class="u-center space-x-2 my-5">
      <OButton
        v-for="(article, index) in articles"
        :key="`page_${index + 1}`"
        pure
        class="rounded-full transition-none w-2 h-2"
        :style="{ background: swiperIndex === index ? '#8197af' : '#d6d8e0' }"
        @click="slideChange(index)"
      ></OButton>
    </div>
  </div>
</template>
<script>
import { defineComponent, ref, computed, onMounted } from '@nuxtjs/composition-api';
import { Navigation } from 'swiper';
import Swiper from '@/modules/sliders/core/Swiper';
import { useTracking } from '@/modules/tracking/composables/useTracking';
import { memoize } from 'lodash';
import { OButton, OIcon } from '~/modules/common/components/ui';
import { useDevice } from '~/composables/useDevice';

export default defineComponent({
  props: {
    arrowColor: {
      type: String,
    },
    articles: {
      type: Array,
    },
    dataLayer: {
      type: Object,
    },
  },
  components: { Swiper, OButton, OIcon },
  setup(props) {
    const { isMobile } = useDevice();
    const { sendDataLayer } = useTracking();
    const swiperRef = ref(null);
    const nextNavBtn = ref(null);
    const prevNavBtn = ref(null);
    const impressionTimeout = ref(null);
    const settings = computed(() => ({
      slidesPerView: isMobile.value ? 1.1 : 1,
      speed: 150,
      loop: true,
      preloadImages: false,
      navigation: {
        nextEl: nextNavBtn,
        prevEl: prevNavBtn,
      },
      breakpoints: {
        640: {
          slidesPerView: 1,
        },
      },
    }));

    const sliderDataLayerGeneratorFn = (item, position) => {
      const list = props.dataLayer?.name;
      return sendDataLayer(
        {
          item,
          list,
          position,
        },
        ['init', 'initOnLoad', 'click']
      );
    };
    const sliderDataLayerGenerator = memoize(sliderDataLayerGeneratorFn, (item, position) => `${item._id}_${position}`);

    onMounted(() => {
      const { initOnLoadEvent: dataLayerSendEvent } = sliderDataLayerGenerator(props.articles[0], 1);
      dataLayerSendEvent();
    });

    const onSwiper = (swiper) => {
      swiperRef.value = swiper;
      swiper.on('slideChange', function () {
        clearTimeout(impressionTimeout.value);
        impressionTimeout.value = setTimeout(() => {
          const slideIndex = swiper.realIndex;
          const { initEvent: dataLayerSendEvent } = sliderDataLayerGenerator(props.articles[slideIndex], slideIndex + 1);
          dataLayerSendEvent({ preventDuplicate: true });
        }, 1000);
      });
      swiper.on('click', function () {
        const slideIndex = parseInt(swiper.clickedSlide.dataset.swiperSlideIndex);
        sliderDataLayerGenerator(props.articles[slideIndex], slideIndex + 1).clickEvent();
      });
    };
    const slideChange = (index) => {
      swiperRef.value?.slideToLoop(index);
    };

    const swiperIndex = computed(() => (swiperRef.value ? swiperRef.value.realIndex : 0));
    return { settings, nextNavBtn, prevNavBtn, Navigation, slideChange, swiperIndex, onSwiper, isMobile };
  },
});
</script>
<style lang="scss">
.articleSlider {
  .swiper-slide {
    height: auto !important;
  }
}
</style>
